import { Component, ChangeDetectionStrategy, inject, signal, afterNextRender, NgZone, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule, CardModule, AlertModule, SpinnerModule, BadgeModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslationModule } from 'i18n';
import { CurrencyAmountPipe, Logger } from 'shared';
import { OrderService } from '../../../../../../services/order.service';
import { OrderWizardTracking, confirmationErrorCategory, paymentErrorCategory } from '../../../order-wizard-tracking';
import { lastValueFrom } from 'rxjs';

// Accept.js is loaded from Authorize.Net's CDN at runtime (sandbox jstest / live
// js host, chosen by the backend). It attaches window.Accept.dispatchData, which
// tokenises the card in the browser into an opaqueData token — the raw card data
// never touches our server (PCI SAQ A-EP).
declare global {
  interface Window {
    Accept?: {
      dispatchData: (
        secureData: unknown,
        callback: (response: AcceptResponse) => void
      ) => void;
    };
  }
}

interface AcceptResponse {
  messages: { resultCode: 'Ok' | 'Error'; message: { code: string; text: string }[] };
  opaqueData?: { dataDescriptor: string; dataValue: string };
}

/**
 * Authorize.Net (Accept.js) confirmation step.
 *
 * Gateway-agnostic like the Stripe/PayPal steps: the backend hands us the public
 * credentials (initiate_payment) and charges the opaqueData token
 * (confirm_authorize_net); this component only collects the card, tokenises it in
 * the browser, and posts the token. Reused across frontends — each supplies its
 * own merchant account via backend env vars, so nothing here is app-specific.
 */
@Component({
  selector: 'app-authorize-net-confirmation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    AlertModule,
    SpinnerModule,
    BadgeModule,
    IconModule,
    TranslationModule,
    CurrencyAmountPipe
  ],
  templateUrl: './authorize-net-confirmation.component.html',
  styleUrls: ['./authorize-net-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthorizeNetConfirmationComponent {
  @Input() orderId: number | null = null;
  @Input() totalPrice: number = 0;
  @Input() paymentCosts: number = 0;
  @Input() totalWithCosts: number = 0;
  @Input() currency: string = 'USD';
  @Output() onSuccess = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();

  protected logger = new Logger('AuthorizeNetConfirmationComponent');
  private orderService = inject(OrderService);
  private zone = inject(NgZone);
  private tracking = inject(OrderWizardTracking, { optional: true });

  // ── Reactive state ──────────────────────────────────────────────
  initializing = signal<boolean>(true);
  processing = signal<boolean>(false);
  errorMessage = signal<string>('');
  ready = signal<boolean>(false);
  mode = signal<string>('sandbox');

  private apiLoginId = '';
  private clientKey = '';
  // Guards against a stuck spinner: set true once a charge attempt is resolved
  // (by Accept.js callback, a thrown dispatch, or the safety timeout).
  private settled = false;

  // Card form fields (bound via ngModel; never sent to our backend).
  cardNumber = '';
  expiry = '';
  cvc = '';
  cardholder = '';
  zip = '';

  constructor() {
    afterNextRender(async () => {
      if (!this.orderId) {
        this.errorMessage.set('Order ID is missing');
        this.initializing.set(false);
        return;
      }
      await this.initialize();
    });
  }

  private async initialize(): Promise<void> {
    this.errorMessage.set('');
    this.initializing.set(true);

    try {
      const response = await lastValueFrom(
        this.orderService.initiatePayment(this.orderId!, 'authorize_net')
      );

      this.apiLoginId = response.authnet_api_login_id || '';
      this.clientKey = response.authnet_client_key || '';
      this.mode.set(response.authnet_mode || 'sandbox');
      if (response.currency) this.currency = response.currency;

      const scriptUrl = response.authnet_accept_js_url;
      if (!this.apiLoginId || !this.clientKey || !scriptUrl) {
        throw new Error('Invalid card payment configuration from server');
      }

      await this.loadAcceptJs(scriptUrl);

      this.ready.set(true);
      this.initializing.set(false);
    } catch (err: any) {
      this.logger.error('Authorize.Net init error:', err);
      this.errorMessage.set(err?.error?.error || err?.message || 'Failed to initialize card payment. Please try again.');
      this.tracking?.step('payment_confirmation', 'error', confirmationErrorCategory(err));
      this.initializing.set(false);
    }
  }

  // Load Accept.js once. It attaches window.Accept asynchronously after the
  // script's onload, so we poll until dispatchData is callable.
  private loadAcceptJs(url: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (window.Accept?.dispatchData) return resolve();

      const finish = () => {
        const start = Date.now();
        const poll = () => {
          if (window.Accept?.dispatchData) return resolve();
          if (Date.now() - start > 10000) return reject(new Error('Accept.js failed to load'));
          setTimeout(poll, 100);
        };
        poll();
      };

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
      if (existing) {
        finish();
        return;
      }

      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => finish();
      script.onerror = () => reject(new Error('Accept.js could not be loaded'));
      document.head.appendChild(script);
    });
  }

  canPay(): boolean {
    return !this.processing() &&
      this.cardNumber.replace(/\s/g, '').length >= 12 &&
      /^\d{1,2}\s*\/\s*\d{2,4}$/.test(this.expiry) &&
      this.cvc.length >= 3;
  }

  pay(): void {
    if (!this.canPay() || !window.Accept?.dispatchData) return;

    this.tracking?.step('payment_confirmation', 'submit');
    this.processing.set(true);
    this.errorMessage.set('');
    this.settled = false;

    const [month, year] = this.expiry.split('/').map(s => s.trim());
    // Only send fields that have a value — passing `undefined` keys makes
    // Accept.js throw synchronously, which would leave the spinner stuck.
    const cardData: Record<string, string> = {
      cardNumber: this.cardNumber.replace(/\s/g, ''),
      month,
      year,
      cardCode: this.cvc.trim()
    };
    const zip = this.zip.trim();
    const fullName = this.cardholder.trim();
    if (zip) cardData['zip'] = zip;
    if (fullName) cardData['fullName'] = fullName;

    const secureData = {
      authData: { clientKey: this.clientKey, apiLoginID: this.apiLoginId },
      cardData
    };

    // Safety net: if Accept.js never invokes the callback, don't spin forever.
    const timer = setTimeout(() => this.zone.run(() => this.resolve(() => {
      this.errorMessage.set('authorize_net_timeout');
      this.tracking?.step('payment_confirmation', 'error', 'network');
    })), 30000);

    try {
      window.Accept.dispatchData(secureData, (response) =>
        this.zone.run(() => this.resolve(() => this.handleAcceptResponse(response), timer))
      );
    } catch (err: any) {
      // Accept.js can throw synchronously on malformed input.
      this.logger.error('Accept.dispatchData threw:', err);
      this.resolve(() => {
        this.errorMessage.set('authorize_net_invalid_card');
        this.tracking?.step('payment_confirmation', 'error', 'validation');
      }, timer);
    }
  }

  // Run a resolution exactly once, clearing the spinner + timeout. Prevents a
  // late callback from re-opening an already-resolved attempt.
  private resolve(action: () => void, timer?: ReturnType<typeof setTimeout>): void {
    if (this.settled) return;
    this.settled = true;
    if (timer) clearTimeout(timer);
    this.processing.set(false);
    action();
  }

  private async handleAcceptResponse(response: AcceptResponse): Promise<void> {
    // Unexpected shape must not throw here (would strand the spinner) — guard all.
    if (response?.messages?.resultCode === 'Error' || !response?.opaqueData) {
      // Accept.js tokenisation failed: the card data was rejected before any charge.
      this.tracking?.step('payment_confirmation', 'error', 'validation');
      this.errorMessage.set(response?.messages?.message?.[0]?.text || 'authorize_net_invalid_card');
      return;
    }

    // From here the charge is in flight again — re-arm the spinner.
    this.processing.set(true);
    try {
      const res = await lastValueFrom(
        this.orderService.confirmAuthorizeNetPayment(this.orderId!, response.opaqueData)
      );
      this.onSuccess.emit({
        orderId: this.orderId,
        isFirstOrder: res?.is_first_order || false,
        dateOfJoining: res?.date_of_joining
      });
    } catch (confirmErr: any) {
      this.logger.error('Authorize.Net confirm failed:', confirmErr);
      this.tracking?.step('payment_confirmation', 'error', paymentErrorCategory(confirmErr));
      this.errorMessage.set(confirmErr?.error?.error || 'Your payment could not be processed. Please try again.');
      this.processing.set(false);
    }
  }

  goBack(): void {
    this.onBack.emit();
  }

  async retry(): Promise<void> {
    this.errorMessage.set('');
    await this.initialize();
  }
}
